/* eslint-disable camelcase */
import React, { useCallback, useState } from "react"
import { observer } from "mobx-react-lite"
import { Field, Formik } from "formik"
import { Appearance, Dimensions, Platform, TouchableOpacity, ViewStyle } from "react-native"
import { FormProps } from "../form"
import * as yup from "yup"
import { TxKeyPath, translate } from "../../../i18n"
import { FormControl } from "../../form-control/form-control"
import { Radio, View, useDisclose, Spinner } from "native-base"
import { Text } from "../../text/text"
import { color } from "../../../theme"
import { PickerActionsheet } from "../../picker-actionsheet/picker-actionsheet"
import JalaAnalytic from "../../../utils/jala-analytic"
import { JalaIcon } from "../../jala-icon/jala-icon"

const FORM_CONTROL: ViewStyle = {
  marginBottom: 4
}

const FORM_DROPDOWN: ViewStyle = {
  marginBottom: 4,
}

const PICKER_CONTAINER: ViewStyle = {
  borderWidth: 1,
  borderColor: color.palette.gray200,
  borderRadius: 5,
  padding: 8,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between"
}

const knowsFromWhere = [
  { code: "online_event" },
  { code: "offline_event" },
  { code: "social_media" },
  { code: "online_ads" },
  { code: "email" },
  { code: "blog_or_article" },
  { code: "offline_attribute" },
  { code: "friend_recomendation" },
  { code: "community" },
  { code: "other" }
]

export interface OccupationInputFormProps extends FormProps {
  initialValues?: {
    occupation?: string,
    state?: {
      know_jala_from?: string
    }
  },
  loading?: boolean,
  occupations?: any,
  getOccupation?: (value?: string) => void
}

/**
 * Describe your component here
 */
export const OccupationInputForm = observer(function OccupationInputForm(props: OccupationInputFormProps) {
  const { initialValues = {}, onSubmit, colorScheme = "light" || Appearance.getColorScheme(), innerRef, loading = false, occupations = [], getOccupation, ...rest } = props
  const [ type, setType ] = useState<"Farmer"|"Non Farmer">(null)
  const { isOpen, onOpen, onClose } = useDisclose()
  const { isOpen: isOpenOccupation, onOpen: onOpenOccupation, onClose: onCloseOccupation } = useDisclose()

  const input = {
    //
  }

  const validationSchema = yup.object({
    occupation: yup.string().nullable().required().label(translate("registerForm.occupation")),
    state: yup.object().shape({
      know_jala_from: yup.string().ascii().nullable().required().label(translate("registerForm.knowjalafrom"))
    }),
  })

  const renderItemPicker = (item) => {
    return <View minHeight="44px" flexDirection="row" alignItems="center" px="16px">
      <Text text={item.item ? item.item.name_translated : "-"} preset="body2" color={color.palette.gray800} />
    </View>
  }

  const occupationValue = useCallback((value) => {
    const occupation = occupations.length ? occupations.find((item) => item.code === value) : null
    return occupation?.name_translated || translate('registerForm.occupationPlaceholder')
  }, [occupations])

  return (
    <Formik
      enableReinitialize
      initialValues={{
        birthdate: null,
        occupation: null,
        ...initialValues
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ setFieldValue }) => (
        <>
          <FormControl
            labelTx="registerForm.type"
            colorScheme={colorScheme}
            labelColor={colorScheme === "light" ? color.palette.gray800 : color.palette.gray200}
            style={FORM_CONTROL}
            isRequired
          >
            <Radio.Group
              colorScheme={colorScheme}
              name="type"
              accessibilityLabel={translate("registerForm.type")}
              value={type}
              onChange={(value: any) => {
                setType(value)
                getOccupation(value)
                setFieldValue('occupation', null)
              }}
            >
              <Radio value="Farmer" mr="32px" mb="8px">
                <Text tx="registerForm.farmers" preset="body2" ml="6px" colorScheme={colorScheme} />
              </Radio>
              <Radio value="Non Farmer">
                <Text tx="registerForm.nonFarmers" preset="body2" ml="6px" colorScheme={colorScheme} />
              </Radio>
            </Radio.Group>
          </FormControl>
          <Field
            key={`${type}-occupation`}
            name="occupation"
          >
            {({ field, form: { setFieldValue, setFieldTouched }, meta }) => (
              <FormControl
                isInvalid={meta.touched && !!meta.error}
                labelTx="registerForm.occupation"
                colorScheme={colorScheme}
                inputProps={{
                  testID: `jala:id/${field.name}-input`,
                  ref: input[field.name],
                  autoCapitalize: "none",
                  autoCorrect: false,
                  defaultValue: meta.initialValue,
                }}
                errorMessage={meta.error}
                style={FORM_DROPDOWN}
                isRequired
                // {...formControlProps}
              >
                <TouchableOpacity style={{...PICKER_CONTAINER, backgroundColor: type ? color.palette.white : color.palette.gray100}} onPress={onOpenOccupation} disabled={loading || !type}>
                  <Text text={occupationValue(field.value)} color={field.value ? color.palette.gray800 : color.palette.gray500} preset="body2" />
                  {loading ? <Spinner /> : <JalaIcon name="expand_more" color={color.palette.gray800} size={24} />}
                </TouchableOpacity>
                <PickerActionsheet
                  colorScheme="light"
                  isOpen={isOpenOccupation}
                  onClose={onCloseOccupation}
                  data={occupations.filter((i) => i.type === type)}
                  titleTx="picker.choose"
                  withoutSearch
                  onPressItem={(item) => async () => {
                    await setFieldValue(field.name, item.code)
                    await setFieldTouched(field.name)
                    JalaAnalytic.log("Mobile App : Pop up reminder - Occupation")
                  }}
                  renderItem={renderItemPicker}
                  flatListProps={{
                    keyExtractor: (item, index) => `${item.id || index}`,
                    estimatedItemSize: 44,
                  }}
                  // eslint-disable-next-line react-native/no-inline-styles
                  actionsheetStyle={{
                    minHeight: "40%",
                    ...(Platform.OS === 'web' ? { maxHeight: Dimensions.get("window").height * 2.5 } : {}),
                  }}
                />
              </FormControl>
            )}
          </Field>
          <Field name="state.know_jala_from">
            {({ field, form: { setFieldValue, setFieldTouched }, meta }) => (
              <FormControl
                isInvalid={meta.touched && !!meta.error}
                labelTx="registerForm.knowjalafrom"
                colorScheme={colorScheme}
                inputProps={{
                  testID: `jala:id/${field.name}-input`,
                  ref: input[field.name],
                  autoCapitalize: "none",
                  autoCorrect: false,
                  defaultValue: meta.initialValue,
                }}
                errorMessage={meta.error}
                style={FORM_DROPDOWN}
                isRequired
                // {...formControlProps}
              >
                <TouchableOpacity style={PICKER_CONTAINER} onPress={onOpen}>
                  <Text text={field.value ? translate(`registerForm.${field.value}` as TxKeyPath) : translate("picker.choose")} color={field.value ? color.palette.gray800 : color.palette.gray500} preset="body2" />
                  <JalaIcon name="expand_more" color={color.palette.gray800} size={24} />
                </TouchableOpacity>
                <PickerActionsheet
                  colorScheme="light"
                  isOpen={isOpen}
                  onClose={onClose}
                  data={knowsFromWhere}
                  titleTx="picker.choose"
                  withoutSearch
                  onPressItem={(item) => async () => {
                    await setFieldValue(field.name, item.code)
                    await setFieldTouched(field.name)
                    JalaAnalytic.log("Mobile App : Pop up reminder - how you know jala")
                  }}
                  renderItem={(item) => (
                    <View minHeight="44px" flexDirection="row" alignItems="center" px="16px">
                      <Text text={item.item ? translate(`registerForm.${item.item.code}` as TxKeyPath) : "-"} preset="body2" color={color.palette.gray800} />
                    </View>
                  )}
                  flatListProps={{
                    keyExtractor: (item) => `${item.code}`,
                    estimatedItemSize: 44,
                  }}
                  // eslint-disable-next-line react-native/no-inline-styles
                  actionsheetStyle={{
                    minHeight: "40%",
                    ...(Platform.OS === 'web' ? { maxHeight: Dimensions.get("window").height * 2.5 } : {}),
                  }}
                />
              </FormControl>
            )}
          </Field>
        </>
      )}
    </Formik>
  )
})

OccupationInputForm.displayName = 'OccupationInputForm'
